import React, { useEffect, useState } from 'react';
import {
  Box, CircularProgress, Dialog, DialogTitle, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { CloseRounded as CloseIcon, DownloadOutlined as DownloadIcon } from '@mui/icons-material';
import DocumentService from '../../api/services/document.service';
import FilePreview, { FilePreviewError } from '../../components/files/FilePreview';
import { downloadBlob } from '../../utils/download';

// A week's file (the statement, the weekly service) opened over the page,
// so looking at it does not leave the week. Closes with the X or Escape.
const DocumentPreviewDialog: React.FC<{
  documentId: string | null;
  title: string;
  onClose: () => void;
}> = ({ documentId, title, onClose }) => {
  const [file, setFile] = useState<{ blob: Blob; url: string; name: string; path: string; contentType: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFile(null);
    setError(null);
    if (!documentId) return undefined;
    let live = true;
    let url: string | null = null;
    Promise.all([DocumentService.getDetail(documentId), DocumentService.getFileBlob(documentId)])
      .then(([{ document }, blob]) => {
        if (!live) return;
        url = URL.createObjectURL(blob);
        setFile({
          blob, url, name: document.fileName || '', path: document.file?.path || '', contentType: document.file?.contentType || blob.type,
        });
      })
      .catch(() => { if (live) setError('This file could not be opened.'); });
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [documentId]);

  const download = () => {
    if (!file || !documentId) return;
    downloadBlob(file.blob, file.name || title);
    DocumentService.recordShared(documentId, 'download');
  };

  return (
    <Dialog
      open={!!documentId}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '85vh' } }}
    >
      <DialogTitle component="div" sx={{ py: 1.25, pr: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" component="h2" fontWeight={600} noWrap>{title}</Typography>
            {file?.name && <Typography variant="caption" color="text.secondary" noWrap component="div">{file.name}</Typography>}
          </Box>
          <Tooltip title="Download">
            <span>
              <IconButton aria-label="Download" onClick={download} disabled={!file}><DownloadIcon /></IconButton>
            </span>
          </Tooltip>
          <IconButton aria-label="Close" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <Box sx={{
        flex: 1, minHeight: 0, display: 'flex', bgcolor: 'grey.100', borderTop: '1px solid', borderColor: 'divider',
      }}
      >
        {error ? (
          <FilePreviewError message={error} />
        ) : !file ? (
          <Box sx={{ m: 'auto' }}><CircularProgress /></Box>
        ) : (
          <FilePreview blob={file.blob} url={file.url} contentType={file.contentType} name={file.path || file.name} title={title} onDownload={download} />
        )}
      </Box>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
